import { useEffect, useRef } from "react";

import { useLazyQuery } from "@apollo/client";

import { POLLING_INTERVAL } from "../../constants";
import type { getActiveSession } from "../../graphql/__generated__/getActiveSession";
import { GET_ACTIVE_SESSION } from "../../graphql/GET_ACTIVE_SESSION";

import type { UseActiveSessionPollingProps, UseActiveSessionPollingReturn } from "./types";

/**
 * Hook для периодического опроса активной сессии пользователя
 *
 * Опрашивает сервер каждые 10 секунд для получения информации о текущей активной сессии.
 * Автоматически останавливает опрос при размонтировании компонента или когда shouldPoll = false.
 */
export const useActiveSessionPolling = ({
  shouldPoll,
  isUserLoggedIn,
}: UseActiveSessionPollingProps): UseActiveSessionPollingReturn => {
  const [getActiveSession, { loading, error, data }] = useLazyQuery<getActiveSession>(
    GET_ACTIVE_SESSION,
    { fetchPolicy: "no-cache" },
  );

  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Управление циклом опроса
  useEffect(() => {
    if (shouldPoll && isUserLoggedIn) {
      // Первый запрос сразу
      if (!pollingTimerRef.current) {
        getActiveSession();

        // Устанавливаем интервал для последующих запросов
        pollingTimerRef.current = setInterval(() => {
          getActiveSession();
        }, POLLING_INTERVAL);
      }
      return;
    }

    // Останавливаем опрос
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, [shouldPoll, isUserLoggedIn, getActiveSession]);

  // Очистка при размонтировании
  useEffect(
    () =>
      () => {
        if (pollingTimerRef.current) {
          clearInterval(pollingTimerRef.current);
          pollingTimerRef.current = null;
        }
      },
    [],
  );

  return { data, loading, error };
};
